import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import { getOrganizationByCommerce, type CoreOrganization } from '@/lib/zifekCore';
import CategoriesTab from './components/CategoriesTab';
import TagsTab from './components/TagsTab';
import DocumentsTab from './components/DocumentsTab';
import MediaTab from './components/MediaTab';
import CalendarTab from './components/CalendarTab';
import AvailabilityTab from './components/AvailabilityTab';
import StatusesTab from './components/StatusesTab';
import NumberingTab from './components/NumberingTab';
import CurrenciesTab from './components/CurrenciesTab';
import CountriesTab from './components/CountriesTab';
import LanguagesTab from './components/LanguagesTab';
import TimezoneTab from './components/TimezoneTab';
import NotificationsTab from './components/NotificationsTab';
import CommunicationTab from './components/CommunicationTab';
import TimelineTab from './components/TimelineTab';
import NotesTab from './components/NotesTab';
import CustomFieldsTab from './components/CustomFieldsTab';
import RelationsTab from './components/RelationsTab';
import RolesPermissionsTab from './components/RolesPermissionsTab';
import ApiTab from './components/ApiTab';
import EventsTab from './components/EventsTab';
import WorkflowsTab from './components/WorkflowsTab';
import AuditTab from './components/AuditTab';
import TrashTab from './components/TrashTab';
import SearchTab from './components/SearchTab';
import AiGatewayTab from './components/AiGatewayTab';
import ActionLayerTab from './components/ActionLayerTab';
import IdempotenceTab from './components/IdempotenceTab';
import ConcurrencyTab from './components/ConcurrencyTab';
import StorageTab from './components/StorageTab';
import SettingsTab from './components/SettingsTab';
import OnboardingTab from './components/OnboardingTab';
import BusinessProfileTab from './components/BusinessProfileTab';
import VerificationTab from './components/VerificationTab';
import CoreDashboardTab from './components/CoreDashboardTab';
import ReviewsTab from './components/ReviewsTab';
import FavoritesTab from './components/FavoritesTab';
import ConsentsTab from './components/ConsentsTab';
import PrivacyTab from './components/PrivacyTab';
import ModulesTab from './components/ModulesTab';
import FeatureFlagsTab from './components/FeatureFlagsTab';
import ApiVersionsTab from './components/ApiVersionsTab';
import RateLimitsTab from './components/RateLimitsTab';
import CacheTab from './components/CacheTab';
import PerformanceTab from './components/PerformanceTab';
import ObservabilityTab from './components/ObservabilityTab';
import BackupTab from './components/BackupTab';
import IntegrityTab from './components/IntegrityTab';
import TransactionsTab from './components/TransactionsTab';
import MigrationsTab from './components/MigrationsTab';
import SeedDataTab from './components/SeedDataTab';
import SchemaTab from './components/SchemaTab';
import BoundariesTab from './components/BoundariesTab';
import CustomerContextTab from './components/CustomerContextTab';
import ScenariosTab from './components/ScenariosTab';
import AiSecurityTab from './components/AiSecurityTab';
import FoundationTab from './components/FoundationTab';

type Tab = 'dashboard' | 'categories' | 'tags' | 'documents' | 'media' | 'calendar' | 'availability' | 'statuses' | 'numbering' | 'currencies' | 'countries' | 'languages' | 'timezone' | 'notifications' | 'communication' | 'timeline' | 'notes' | 'customfields' | 'relations' | 'roles' | 'api' | 'events' | 'workflows' | 'audit' | 'trash' | 'search' | 'aigateway' | 'actionlayer' | 'idempotence' | 'concurrency' | 'storage' | 'settings' | 'onboarding' | 'businessprofile' | 'verification' | 'reviews' | 'favorites' | 'consents' | 'privacy' | 'modules' | 'featureflags' | 'apiversions' | 'ratelimits' | 'cache' | 'performance' | 'observability' | 'backup' | 'integrity' | 'transactions' | 'migrations' | 'seed' | 'schema' | 'boundaries' | 'customercontext' | 'scenarios' | 'aisecurity' | 'foundation';

const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: 'dashboard', label: 'Tableau de bord', icon: 'ri-dashboard-line' },
  { key: 'categories', label: 'Catégories', icon: 'ri-price-tag-3-line' },
  { key: 'tags', label: 'Tags', icon: 'ri-hashtag' },
  { key: 'documents', label: 'Documents', icon: 'ri-file-text-line' },
  { key: 'media', label: 'Média', icon: 'ri-image-2-line' },
  { key: 'calendar', label: 'Calendrier', icon: 'ri-calendar-2-line' },
  { key: 'availability', label: 'Disponibilités', icon: 'ri-time-line' },
  { key: 'statuses', label: 'Statuts', icon: 'ri-shield-check-line' },
  { key: 'numbering', label: 'Numérotation', icon: 'ri-hashtag' },
  { key: 'currencies', label: 'Devises', icon: 'ri-money-dollar-circle-line' },
  { key: 'countries', label: 'Pays & Localisation', icon: 'ri-earth-line' },
  { key: 'languages', label: 'Langues', icon: 'ri-translate-2' },
  { key: 'timezone', label: 'Fuseaux horaires', icon: 'ri-global-line' },
  { key: 'notifications', label: 'Notifications', icon: 'ri-notification-3-line' },
  { key: 'communication', label: 'Communication', icon: 'ri-chat-3-line' },
  { key: 'timeline', label: 'Timeline', icon: 'ri-history-line' },
  { key: 'notes', label: 'Notes', icon: 'ri-sticky-note-line' },
  { key: 'customfields', label: 'Champs perso.', icon: 'ri-input-field' },
  { key: 'relations', label: 'Relations', icon: 'ri-links-line' },
  { key: 'roles', label: 'Rôles & Permissions', icon: 'ri-user-settings-line' },
  { key: 'api', label: 'API & Webhooks', icon: 'ri-code-s-slash-line' },
  { key: 'events', label: 'Événements', icon: 'ri-flashlight-line' },
  { key: 'workflows', label: 'Workflows', icon: 'ri-flow-chart' },
  { key: 'audit', label: 'Audit', icon: 'ri-file-shield-2-line' },
  { key: 'trash', label: 'Corbeille', icon: 'ri-delete-bin-7-line' },
  { key: 'search', label: 'Recherche', icon: 'ri-search-line' },
  { key: 'aigateway', label: 'Passerelle AI', icon: 'ri-robot-2-line' },
  { key: 'actionlayer', label: 'Actions', icon: 'ri-flashlight-line' },
  { key: 'idempotence', label: 'Idempotence', icon: 'ri-loop-right-line' },
  { key: 'concurrency', label: 'Concurrence', icon: 'ri-lock-2-line' },
  { key: 'storage', label: 'Stockage', icon: 'ri-hard-drive-2-line' },
  { key: 'settings', label: 'Paramètres', icon: 'ri-settings-3-line' },
  { key: 'onboarding', label: 'Onboarding', icon: 'ri-rocket-2-line' },
  { key: 'businessprofile', label: 'Profil business', icon: 'ri-store-2-line' },
  { key: 'verification', label: 'Vérification', icon: 'ri-shield-check-line' },
  { key: 'reviews', label: 'Avis', icon: 'ri-chat-quote-line' },
  { key: 'favorites', label: 'Favoris', icon: 'ri-bookmark-3-line' },
  { key: 'consents', label: 'Consentements', icon: 'ri-shield-user-line' },
  { key: 'privacy', label: 'Confidentialité', icon: 'ri-lock-2-line' },
  { key: 'modules', label: 'Modules & Extensions', icon: 'ri-apps-2-line' },
  { key: 'featureflags', label: 'Feature Flags', icon: 'ri-toggle-line' },
  { key: 'apiversions', label: 'Versions API', icon: 'ri-git-branch-line' },
  { key: 'ratelimits', label: 'Rate limiting', icon: 'ri-shield-check-line' },
  { key: 'cache', label: 'Cache', icon: 'ri-database-2-line' },
  { key: 'performance', label: 'Performance', icon: 'ri-speed-up-line' },
  { key: 'observability', label: 'Observabilité', icon: 'ri-pulse-line' },
  { key: 'backup', label: 'Sauvegardes', icon: 'ri-archive-2-line' },
  { key: 'integrity', label: 'Intégrité', icon: 'ri-node-tree' },
  { key: 'transactions', label: 'Transactions', icon: 'ri-loop-right-line' },
  { key: 'migrations', label: 'Migrations', icon: 'ri-git-commit-line' },
  { key: 'seed', label: 'Données par défaut', icon: 'ri-seedling-line' },
  { key: 'schema', label: 'Architecture', icon: 'ri-layout-grid-line' },
  { key: 'boundaries', label: 'Frontières Core', icon: 'ri-scissors-cut-line' },
  { key: 'customercontext', label: 'Contexte client', icon: 'ri-node-tree' },
  { key: 'scenarios', label: 'Scénarios', icon: 'ri-git-branch-line' },
  { key: 'aisecurity', label: 'Sécurité IA', icon: 'ri-shield-keyhole-line' },
  { key: 'foundation', label: 'Fondation', icon: 'ri-anchor-line' },
];

export default function DashboardSoclePage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') as Tab | null;
  const [tab, setTab] = useState<Tab>(
    requestedTab && TABS.some((t) => t.key === requestedTab) ? requestedTab : 'dashboard',
  );
  const [org, setOrg] = useState<CoreOrganization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const commerceId = useMemo(() => getCommerceId(user), [user]);

  const load = useCallback(async () => {
    if (!commerceId) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      setOrg(await getOrganizationByCommerce(commerceId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger le socle central.');
    } finally {
      setLoading(false);
    }
  }, [commerceId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-stack-line text-primary-600"></i>
            Socle central
          </h2>
          <p className="text-sm text-foreground-500 mt-1">
            Les briques communes réutilisées par tous vos modules : catégories, tags, documents, médias et calendrier.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 disabled:opacity-50 transition-colors"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          Actualiser
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center justify-between gap-3">
          <span className="flex items-center gap-2"><i className="ri-error-warning-line"></i>{error}</span>
          <button onClick={load} className="text-xs underline cursor-pointer whitespace-nowrap">Réessayer</button>
        </div>
      ) : !org ? (
        <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-background-200/70 rounded-lg text-center px-6">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-building-2-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-700 font-medium">Votre organisation n’est pas encore initialisée.</p>
          <p className="text-sm text-foreground-500 mt-1 max-w-md">
            Le socle de votre entreprise est créé automatiquement à l’inscription. Contactez le support si le problème persiste.
          </p>
          <button onClick={load} className="mt-4 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            Réessayer
          </button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-5 w-full overflow-x-auto">
            {TABS.map((tb) => (
              <button
                key={tb.key}
                onClick={() => setTab(tb.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  tab === tb.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className={tb.icon}></i>
                {tb.label}
              </button>
            ))}
          </div>

          {commerceId && tab === 'categories' && <CategoriesTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'tags' && <TagsTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'documents' && <DocumentsTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'media' && <MediaTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'calendar' && <CalendarTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'availability' && <AvailabilityTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'statuses' && <StatusesTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'numbering' && <NumberingTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'currencies' && <CurrenciesTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'countries' && <CountriesTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'languages' && <LanguagesTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'timezone' && <TimezoneTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'notifications' && <NotificationsTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'communication' && <CommunicationTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'timeline' && <TimelineTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'notes' && <NotesTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'customfields' && <CustomFieldsTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'relations' && <RelationsTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'roles' && <RolesPermissionsTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'api' && <ApiTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'events' && <EventsTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'workflows' && <WorkflowsTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'audit' && <AuditTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'trash' && <TrashTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'search' && <SearchTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'aigateway' && <AiGatewayTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'actionlayer' && <ActionLayerTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'idempotence' && <IdempotenceTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'concurrency' && <ConcurrencyTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'storage' && <StorageTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'settings' && <SettingsTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'onboarding' && <OnboardingTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'businessprofile' && <BusinessProfileTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'verification' && <VerificationTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'dashboard' && <CoreDashboardTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'reviews' && <ReviewsTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'favorites' && <FavoritesTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'consents' && <ConsentsTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'privacy' && <PrivacyTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'modules' && <ModulesTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'featureflags' && <FeatureFlagsTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'apiversions' && <ApiVersionsTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'ratelimits' && <RateLimitsTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'cache' && <CacheTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'performance' && <PerformanceTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'observability' && <ObservabilityTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'backup' && <BackupTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'integrity' && <IntegrityTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'transactions' && <TransactionsTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'migrations' && <MigrationsTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'seed' && <SeedDataTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'schema' && <SchemaTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'boundaries' && <BoundariesTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'customercontext' && <CustomerContextTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'scenarios' && <ScenariosTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'aisecurity' && <AiSecurityTab commerceId={commerceId} org={org} />}
          {commerceId && tab === 'foundation' && <FoundationTab commerceId={commerceId} org={org} />}
        </>
      )}
    </div>
  );
}